// src/components/files/SharedView.tsx
import React from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActionArea,
  Avatar,
  Chip,
  alpha,
} from '@mui/material';
import FolderSharedIcon from '@mui/icons-material/FolderShared';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import { useTranslation } from 'react-i18next';
import { useFileManager } from '../../contexts/FileManagerContext';
import EmptyState from './EmptyState';

const SharedView: React.FC = () => {
  const { t } = useTranslation();
  const { state, navigateToFolder } = useFileManager();
  const { sharedWithMe } = state;

  if (sharedWithMe.length === 0) {
    return (
      <EmptyState
        icon={<FolderSharedIcon sx={{ fontSize: 80, color: 'grey.400' }} />}
        title={t('fileManager.shared.empty', 'Aucun dossier partage')}
        description={t('fileManager.shared.emptyDescription', 'Les dossiers partages avec vous apparaitront ici')}
      />
    );
  }

  const getPermissionIcon = (permission: string) => {
    switch (permission) {
      case 'read':
        return <VisibilityIcon sx={{ fontSize: 14 }} />;
      case 'write':
        return <EditIcon sx={{ fontSize: 14 }} />;
      case 'admin':
        return <AdminPanelSettingsIcon sx={{ fontSize: 14 }} />;
      default:
        return <VisibilityIcon sx={{ fontSize: 14 }} />;
    }
  };

  const getPermissionLabel = (permission: string) => {
    switch (permission) {
      case 'read':
        return t('fileManager.shared.readOnly', 'Lecture seule');
      case 'write':
        return t('fileManager.shared.canEdit', 'Peut modifier');
      case 'admin':
        return t('fileManager.shared.admin', 'Administrateur');
      default:
        return permission;
    }
  };

  const getPermissionColor = (permission: string) => {
    switch (permission) {
      case 'read':
        return { bg: alpha('#64748b', 0.1), color: '#64748b' };
      case 'write':
        return { bg: alpha('#2563eb', 0.1), color: '#2563eb' };
      case 'admin':
        return { bg: alpha('#16a34a', 0.1), color: '#16a34a' };
      default:
        return { bg: alpha('#64748b', 0.1), color: '#64748b' };
    }
  };

  const handleFolderClick = (folderId: number) => {
    navigateToFolder(folderId);
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" fontWeight={600}>
          {t('fileManager.shared.title', 'Partages avec moi')}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t('fileManager.shared.subtitle', '{{count}} dossier(s) partage(s)', { count: sharedWithMe.length })}
        </Typography>
      </Box>

      {/* Shared folders grid */}
      <Grid container spacing={2}>
        {sharedWithMe.map((share) => {
          const permColors = getPermissionColor(share.permission);
          return (
            <Grid item xs={12} sm={6} md={4} lg={3} key={share.share_id}>
              <Card
                sx={{
                  borderRadius: 3,
                  border: '1px solid',
                  borderColor: 'grey.200',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    borderColor: 'primary.main',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                    transform: 'translateY(-2px)',
                  },
                }}
              >
                <CardActionArea onClick={() => handleFolderClick(share.folder.id)}>
                  <CardContent sx={{ p: 2.5 }}>
                    {/* Folder icon */}
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 56,
                        height: 56,
                        borderRadius: 2,
                        bgcolor: alpha(share.folder.color || '#2563eb', 0.1),
                        mb: 2,
                      }}
                    >
                      <FolderSharedIcon
                        sx={{
                          fontSize: 32,
                          color: share.folder.color || '#2563eb',
                        }}
                      />
                    </Box>

                    {/* Folder name */}
                    <Typography
                      variant="subtitle1"
                      fontWeight={600}
                      sx={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        mb: 1,
                      }}
                    >
                      {share.folder.name}
                    </Typography>

                    {/* Permission chip */}
                    <Chip
                      icon={getPermissionIcon(share.permission)}
                      label={getPermissionLabel(share.permission)}
                      size="small"
                      sx={{
                        bgcolor: permColors.bg,
                        color: permColors.color,
                        fontWeight: 500,
                        '& .MuiChip-icon': { color: permColors.color },
                        mb: 2,
                      }}
                    />

                    {/* Shared by */}
                    {share.shared_by && (
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          pt: 2,
                          borderTop: '1px solid',
                          borderColor: 'grey.100',
                        }}
                      >
                        <Avatar
                          sx={{
                            width: 24,
                            height: 24,
                            bgcolor: 'primary.main',
                            fontSize: '0.75rem',
                          }}
                        >
                          {share.shared_by.name?.charAt(0).toUpperCase() || '?'}
                        </Avatar>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: 'block' }}
                          >
                            {t('fileManager.shared.sharedBy', 'Partage par')}
                          </Typography>
                          <Typography
                            variant="body2"
                            fontWeight={500}
                            sx={{
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {share.shared_by.name}
                          </Typography>
                        </Box>
                      </Box>
                    )}

                    {/* Shared date */}
                    {share.shared_at && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                        {t('fileManager.shared.sharedOn', 'Partage le')} {new Date(share.shared_at).toLocaleDateString()}
                      </Typography>
                    )}

                    {/* Expiration warning */}
                    {share.expires_at && (
                      <Chip
                        label={t('fileManager.shared.expires', 'Expire le {{date}}', {
                          date: new Date(share.expires_at).toLocaleDateString(),
                        })}
                        size="small"
                        color="warning"
                        variant="outlined"
                        sx={{ mt: 1 }}
                      />
                    )}
                  </CardContent>
                </CardActionArea>
              </Card>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
};

export default SharedView;
